import {
  Toaster as BlueprintToaster,
  Intent,
  Position,
} from '@blueprintjs/core';
import type { IToaster } from '@blueprintjs/core';

import './style.scss';

let CopyToaster: IToaster;

// Checking document here as when imported into portal-app, the production build
// throws "ReferenceError: document is not defined"
if (typeof document !== 'undefined') {
  CopyToaster = BlueprintToaster.create({
    autoFocus: true,
    className: 'high-z-index-toaster',
    maxToasts: 1,
    position: Position.TOP,
  });
}

const SEC = 1000;

const info = (message: string) => {
  CopyToaster.show({
    intent: Intent.NONE,
    message,
  });
};

const success = (message: string) => {
  CopyToaster.show({
    icon: 'tick',
    intent: Intent.SUCCESS,
    message,
    timeout: SEC * 2,
  });
};

const error = (message: string) => {
  CopyToaster.show({
    intent: Intent.DANGER,
    message,
    timeout: SEC * 10,
  });
};

const warn = (message: string) => {
  CopyToaster.show({
    intent: Intent.WARNING,
    message,
    timeout: SEC * 10,
  });
};

const createFailure = (resourceName: string) =>
  error(`Unable to create ${resourceName}`);

const createLoading = (resourceName: string) =>
  info(`Creating ${resourceName}...`);

const createSuccess = (resourceName: string) =>
  success(`Successfully created ${resourceName}!`);

const deleteFailure = (resourceName: string) =>
  error(`Unable to delete ${resourceName}`);

const deleteLoading = (resourceName: string) =>
  info(`Deleting ${resourceName}...`);

const deleteSuccess = (resourceName: string) =>
  success(`Successfully deleted ${resourceName}!`);

const updateLoading = (resourceName: string) =>
  info(`Updating ${resourceName}...`);

const updateSuccess = (resourceName: string) =>
  success(`Successfully updated ${resourceName}!`);

const updateFailure = (resourceName: string) =>
  error(`Unable to update ${resourceName}`);

export const Toaster = {
  error,
  info,
  success,
  warn,
};

export const CrudToaster = {
  createFailure,
  createLoading,
  createSuccess,
  deleteFailure,
  deleteLoading,
  deleteSuccess,
  updateFailure,
  updateLoading,
  updateSuccess,
};

export const createCrudToaster = (resourceName: string) => {
  const deleteLoading = (resourceName) => info(`Deleting ${resourceName}...`);
  const endLoading = () => info(`Ending ${resourceName}...`);

  const endSuccess = () => success(`Successfully ended ${resourceName}!`);

  const deleteFailure = (resourceName) =>
    error(`Unable to delete ${resourceName}`);

  const deleteSuccess = (resourceName) =>
    success(`Successfully deleted ${resourceName}!`);

  const endFailure = () => error(`Unable to end ${resourceName}`);

  return {
    createFailure: () => createFailure(resourceName),
    createLoading: () => createLoading(resourceName),
    createSuccess: () => createSuccess(resourceName),
    deleteFailure: () => deleteFailure(resourceName),
    deleteLoading: () => deleteLoading(resourceName),
    deleteSuccess: () => deleteSuccess(resourceName),
    endFailure,
    endLoading,
    endSuccess,
    updateFailure: () => updateFailure(resourceName),
    updateLoading: () => updateLoading(resourceName),
    updateSuccess: () => updateSuccess(resourceName),
  };
};
